<template>
  <v-card
    @click="$router.push({ path: `/meetings/${checkin.definitionId}` })"
    :class="{
      'full-height': true,
      'border--info': !overdue && checkin.startDate != null,
      'border--error': overdue
    }"
  >
    <v-card-text
      :class="{
        'py-0': true
      }"
    >
      <v-row>
        <v-col cols="auto" class="my-auto subtitle-2">
          <v-icon color="secondarytext" class="mr-2">
            {{ type.icon }}
          </v-icon>
          {{ type.name }}
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="my-auto" v-if="checkin.archiveDate == null">
          <v-menu absolute>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon>
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <template v-if="allowEdit">
                <v-list-item @click="$emit('edit')">
                  <v-list-item-title>Change Description</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$emit('editattendees')">
                  <v-list-item-title>Manage participants</v-list-item-title>
                </v-list-item>
                <v-list-item @click="archive">
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteCheckIn">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </template>
              <v-list-item v-else @click="leaveCheckIn">
                <v-list-item-title>Leave Meeting</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <div class="font-weight-bold text-h6 pb-1 secondary--text">
            {{ checkin.name }}
          </div>
          <div class="pb-2">
            <template v-if="overdue">
              The last session never ended
            </template>
            <template v-else-if="checkin.startDate != null">
              Meeting is in progress -
              <router-link
                :to="`/meetings/${checkin.definitionId}/start`"
                class="info--text"
                >Join now</router-link
              >
            </template>
            <template v-else>
              Last meeting {{ lastCheckinText(checkin) }}
            </template>
          </div>
          <div class="background rounded-lg">
            <v-row dense>
              <v-col cols="auto">
                <check-in-labels
                  class="pa-2"
                  :checkin="checkin"
                  :overrideColor="$vuetify.theme.themes.light.secondarytext"
                ></check-in-labels>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-row dense>
                  <v-col cols="12" class="text-right pr-4">
                    <v-btn
                      :color="
                        getColor(
                          $vuetify.theme.themes.light.lighttext,
                          allowEdit ? 1 : 0
                        )
                      "
                      icon
                      @click="allowEdit ? $emit('editattendees') : null"
                    >
                      <v-icon small>person_add</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="text-right pr-6">
                    <overlap-avatars
                      :users="attendees"
                      :size="25"
                      :overlap="3"
                      :limit="3"
                      v-on:click="allowEdit ? $emit('editattendees') : null"
                      :class="{ 'cursor-pointer': allowEdit }"
                    ></overlap-avatars>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row justify="space-between">
        <v-col cols="auto" class="px-0">
          <v-btn
            text
            color="error"
            v-if="checkin.archiveDate != null"
            :to="`/meetings/${checkin.definitionId}`"
          >
            <v-icon class="mr-2" small>history</v-icon>
            History
          </v-btn>
          <v-btn
            text
            color="secondarytext"
            v-else-if="checkin.startDate == null"
            :to="`/meetings/${checkin.definitionId}`"
          >
            <v-icon class="mr-2" small>speaker_notes</v-icon>
            View & Build
          </v-btn>
        </v-col>
        <v-col cols="auto" v-if="checkin.archiveDate == null" class="px-0">
          <v-btn
            text
            color="error"
            v-if="overdue && isOwner"
            :to="`/meetings/${checkin.definitionId}/start`"
          >
            <v-icon class="mr-2" small>flag</v-icon>
            End Meeting
          </v-btn>
          <v-btn
            text
            color="info"
            v-else-if="checkin.startDate != null"
            :to="`/meetings/${checkin.definitionId}/start`"
          >
            <v-icon class="mr-2" small>send</v-icon>
            Join Meeting
          </v-btn>
          <v-btn
            text
            color="secondarytext"
            v-else-if="isOwner"
            :to="`/meetings/${checkin.definitionId}/start`"
          >
            <v-icon class="mr-2" small>send</v-icon>
            Start Meeting
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import CheckInLabels from "@/components/checkin/CheckInLabels";
import OverlapAvatars from "@/components/common/OverlapAvatars";
import moment from "moment";
export default {
  name: "CheckInListCard",
  components: {
    CheckInLabels,
    OverlapAvatars
  },
  props: {
    loading: { type: Boolean, default: false },
    checkin: { type: Object }
  },
  data: () => ({
    search: "",
    saving: false
  }),
  computed: {
    allowEdit() {
      return this.isOwner && this.checkin.archiveDate == null;
    },
    isOwner() {
      return this.checkin.ownerIds.includes(this.profile.id);
    },
    type() {
      if (this.checkin.userIdFor1on1) {
        return { icon: "people_outline", name: "1-on-1" };
      }
      return this.attendees.length == 1
        ? { icon: "person_outline", name: "Solo" }
        : { icon: "recent_actors", name: "Group" };
    },
    overdue() {
      return (
        this.checkin.startDate != null &&
        moment(this.checkin.startDate).add(2, "days") <= moment()
      );
    },
    attendees() {
      return this.checkin.attendees || [];
    },
    lastCheckinText() {
      return item => {
        if (item.lastCheckInDays == null) {
          return "never";
        } else if (item.lastCheckInDays == 0) {
          return "today";
        } else if (item.lastCheckInDays == 1) {
          return "yesterday";
        } else {
          return `${item.lastCheckInDays} days ago`;
        }
      };
    },
    ...mapGetters({
      checkins: "checkin/list",
      profile: "profile/profile"
    })
  },
  methods: {
    async archive() {
      if (
        await this.$root.$confirm(
          "Archive meeting",
          "Are you sure you want to archive this meeting?",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("checkin/archiveCheckIn", this.checkin.definitionId)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Meeting archived");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your meeting."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async deleteCheckIn() {
      if (
        await this.$root.$confirm(
          "Delete meeting",
          "Are you sure you want to delete this meeting? This data will not be recoverable",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("checkin/deleteCheckIn", this.checkin.definitionId)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Meeting deleted");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your meeting."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async leaveCheckIn() {
      if (
        await this.$root.$confirm(
          "Leave meeting",
          "Are you sure you want to leave this meeting?",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("checkin/leaveCheckIn", this.checkin.definitionId)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Meeting left");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your meeting."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>
